import { useEffect, useRef } from 'react';
import { getWebsocketService } from '~/services/WebSocketService';
import SocketEventType from '~/constants/socket-event-type';
import { useInvalidateTagsAndCache } from '~/domain/dashboard/hooks/use-invalidate-tags-and-cache';
import { WebSocketCore, WS_VISIBILITY_GRACE_MS } from '~/domain/realtime/utils/web-socket-core';
import {
  createDeferredSuspend,
  WS_PENDING_HOLD_MAX_MS,
} from '~/domain/realtime/utils/deferred-suspend';
import { selectHasPendingRealtimeWork } from '~/domain/realtime/selectors/select-has-pending-realtime-work';
import { VisibilityGate } from '~/core/utils/visibility-gate/visibility-gate';
import { useGetWsIdleDisconnectEnabled } from '~/config/firebase/remote-config';
import Store from '~/store/Store';
import Logger from '~/utils/logger/logger';
import { useWebToken } from './use-websocket-token';

const useWsConnector = () => {
  const fetchToken = useWebToken();
  const websocketService = getWebsocketService(fetchToken);
  const { data: idleDisconnectEnabled } = useGetWsIdleDisconnectEnabled();
  const fetchTokenRef = useRef(fetchToken);
  const invalidateTagsAndCache = useInvalidateTagsAndCache();

  useEffect(() => {
    fetchTokenRef.current = fetchToken;
  }, [fetchToken]);

  useEffect(() => {
    if (idleDisconnectEnabled) {
      return;
    }
    if (websocketService.halted) {
      websocketService.unhalt();
    }
    async function asyncWsConnect() {
      try {
        await websocketService.connect();
      } catch {
        Logger.error('Unable to connect to websocket');
      }
    }
    asyncWsConnect();
    return () => websocketService.disconnect();
  }, [websocketService, idleDisconnectEnabled]);

  useEffect(() => {
    if (!idleDisconnectEnabled) {
      return;
    }
    websocketService.halt();
    const unsubscribeReconnection = websocketService.on(
      SocketEventType.Reconnection,
      () => void invalidateTagsAndCache(),
      false
    );
    const connection = new WebSocketCore({
      endpoint: websocketService.endpoint,
      heartbeatMs: websocketService.getHeartBeatIntervalMs(),
      fetchToken: () => fetchTokenRef.current(),
      canConnect: () =>
        Boolean(websocketService.getIsAuthAvailable()) &&
        Store.getState().authentication.provider !== 'isam',
      emit: (type, data) => websocketService.emit(type, data),
      initialConnectionsCount: websocketService.connectionsCount,
    });
    const deferredSuspend = createDeferredSuspend({
      isBusy: () => selectHasPendingRealtimeWork(Store.getState()),
      subscribe: Store.subscribe,
      suspend: connection.suspend,
      maxHoldMs: WS_PENDING_HOLD_MAX_MS,
    });
    const gate = new VisibilityGate({
      onHiddenGraceExpired: deferredSuspend.request,
      onVisible: () => {
        deferredSuspend.cancel();
        void connection.resume();
      },
      graceMs: WS_VISIBILITY_GRACE_MS,
    });
    void connection.connect();
    gate.start();
    return () => {
      deferredSuspend.cancel();
      gate.stop();
      connection.disconnect();
      websocketService.connectionsCount = connection.connectionsCount;
      unsubscribeReconnection();
    };
  }, [websocketService, idleDisconnectEnabled, invalidateTagsAndCache]);
};

export default useWsConnector;
